import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";

import { SoundExplorerFragment } from "@graphqlTypes";
import { EASE } from "@/core/constants";
import { Image as RHImage } from "@components";
import NextImage from "next/image";

import { useDebug } from '@/utils/useDebugLog';

import className from "classnames/bind";
import styles from "./SoundExplorer.module.scss";
const cx = className.bind(styles);

import Record from "@/assets/images/record-disc.png";
import RecordCenter from "@/assets/svg/record-center.svg";
// import ArrowRightIcon from '@/assets/svg/arrow-right.svg';

type SoundExplorerSlideProps = {
  slide: SoundExplorerFragment["slides"][number];
  index: number;
  activeIndex: number;
  count: number;
  onSlideFocus?: (index: number) => void;
  onTitleHeightChange?: (height: number) => void;
  animationSpeed: number;
}

export default function SoundExplorerSlide({ slide, index, activeIndex, count, onSlideFocus, onTitleHeightChange, animationSpeed }: SoundExplorerSlideProps) {

  const { log } = useDebug({ name: 'SoundExplorerSlide', bgColor: 'pink', enabled: true });

  const isCenter = index === activeIndex;
  const position = index - activeIndex;

  const variants = {
    center: {
      width: "100%",
      opacity: 1,
      y: "0%",
      transition: { duration: 0.4 * animationSpeed, ease: EASE.InOutQuad }
    },
    notCenter: (custom: number) => ({
      width: "73%",
      opacity: 0.7,
      y: `${custom * -40}%`,
      transition: { duration: 0.4 * animationSpeed, ease: EASE.InOutQuad }
    })
  };

  const recordVariants = {
    active: {
      y: "-50%",
      transition: { duration: 0.35 * animationSpeed, delay: 0.25 * animationSpeed, ease: EASE.InOutQuad }
    },
    inactive: {
      y: "0%",
      transition: { duration: 0.35 * animationSpeed, ease: EASE.InOutQuad }
    }
  };

  const handleSlideFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSlideFocus) onSlideFocus(index);
  };

  // -------------------- First render

  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
  }, []);

  // -------------------- Title height

  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!titleRef.current || !onTitleHeightChange) return;

    onTitleHeightChange(titleRef.current.getBoundingClientRect().height);

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      onTitleHeightChange(entry.contentRect.height);
    });
    observer.observe(titleRef.current);

    return () => observer.disconnect();
  }, []);

  // -------------------- Render

  return (
    <motion.div
      key={index}
      className={cx(["slide", { "slide--center": isCenter }])}
      initial={isFirstRender ? false : (isCenter ? "center" : "notCenter")}
      animate={isCenter ? variants.center : variants.notCenter(position)}
      variants={variants}
      custom={position}
    >

      <div className={cx(["slide-image"])} tabIndex={0} onFocus={handleSlideFocus}>

        <motion.div
          className={cx(["slide-record"])}
          animate={isCenter ? recordVariants.active : recordVariants.inactive}
          style={
            {
              "--record-color": slide.albumColor,
            } as React.CSSProperties
          }
          aria-hidden={true}
        >
          <NextImage src={Record} alt="Record" />
          <RecordCenter className={cx(["record-center"])} />
        </motion.div>

        <RHImage
          className={cx(["slide-cover"])}
          image={slide.albumImage}
        />

      </div>

      <div className={cx(["slide-content"])} ref={titleRef}>
        <div className={cx(["slide-content-top"])}>
          <h3 className={cx(["song", "eyebrow-l"])}>{slide.songTitle}</h3>
          <p className={cx(["album", "eyebrow-l"])}>{slide.albumName}</p>
        </div>
        {/* <p className={cx(["description", "text-m", "body"])}>{slide.description}</p> */}
      </div>

    </motion.div>
  )

}
